import React, { useMemo } from 'react';

import { split } from 'lodash';

import {
  IDashboardLayout,
  WidgetProps,
} from 'components/Visualization/CDashboard/types';
import { AlertCountForAllCategories } from 'containers/Findings/FindingsOverview/Components/Cards/AlertCount/AlertCountForAllCategories';
import { AlertCountForEachCategory } from 'containers/Findings/FindingsOverview/Components/Cards/AlertCount/AlertCountForEachCategory';
import { CreatedVsResolvedIssues } from 'containers/Findings/FindingsOverview/Components/Widgets/CreatedVsResolvedIssues';
import { CriticalAlertsChart } from 'containers/Findings/FindingsOverview/Components/Widgets/CriticalAlertsChart';
import { TopOpenAlertsChart } from 'containers/Findings/FindingsOverview/Components/Widgets/TopOpenAlertsChart';
import { renderHeader } from 'containers/Findings/FindingsOverview/utils';
import { useSearchParams } from 'hooks/useSearchParams';
import { toTitleCase } from 'utils/string';

export const useUserDashboard = () => {
  const params = useSearchParams<{ tab: string }>();

  const availableWidgets: Record<string, WidgetProps> = useMemo(() => {
    const currentCategory = params.tab ?? 'Overview';
    return {
      AlertSummary: {
        name: renderHeader(
          currentCategory === 'Overview' || currentCategory === 'AllFindings'
            ? 'Findings Summary'
            : toTitleCase(split(currentCategory as string, '?')[0]),
        ),
        styles: { componentWrapper: { py: 4 } },
        components: {
          content:
            currentCategory === 'Overview' ||
            currentCategory === 'AllFindings' ? (
              <AlertCountForAllCategories />
            ) : (
              <AlertCountForEachCategory category={currentCategory} />
            ),
        },
        className: 'alert_summary',
        w: '18.6%',
      },
      top_open_issues: {
        name: 'Open Findings',
        styles: { componentWrapper: { pb: 4 } },
        components: {
          content: (
            <TopOpenAlertsChart
              alertCategory={
                currentCategory === 'Overview' ||
                currentCategory === 'AllFindings'
                  ? undefined
                  : (currentCategory as string)
              }
            />
          ),
        },
        className: 'top_open_issues',
        w: '24%',
      },
      critical_issues: {
        name: 'Critical and High Severity Findings',
        styles: { componentWrapper: { pt: 0, pb: 4 } },
        components: {
          content: <CriticalAlertsChart />,
        },
        className: 'critical_issues',
        w: '24%',
      },
      created_vs_resolved_issues: {
        name: 'Created vs. Resolved Issues',
        styles: { componentWrapper: { pt: 0, pb: 4 } },
        components: {
          content: <CreatedVsResolvedIssues />,
        },
        className: 'created_vs_resolved_issues',
        w: '24%',
      },
    };
  }, [params.tab]);

  const dashboardWidgets: IDashboardLayout = useMemo(
    () => ({
      id: 'dashboard_1',
      title: 'Dashboard Title',
      widgets: [
        {
          type: 'card',
          i: 'AlertSummary',
          x: 0,
          y: 0,
          w: 2.5,
          h: 6.5,
          minH: 2,
          minW: 2,
          isResizable: false,
          static: true,
        },

        {
          type: 'line-chart',
          i: 'top_open_issues',
          x: 2.5,
          y: 0,
          w: 3.16,
          h: 6.5,
          minH: 2,
          minW: 2,
          isResizable: false,
          static: true,
        },
        {
          type: 'line-chart',
          i: 'critical_issues',
          x: 5.66,
          y: 0,
          w: 3.16,
          h: 6.5,
          minH: 4,
          minW: 2,
          isResizable: false,
          static: true,
        },
        {
          type: 'line-chart',
          i: 'created_vs_resolved_issues',
          x: 8.82,
          y: 0,
          w: 3.16,
          h: 6.5,
          minH: 4,
          minW: 2,
          isResizable: false,
          static: true,
        },
      ],
      available: availableWidgets,
    }),
    [availableWidgets],
  );

  const alertSummaryWidgets: IDashboardLayout = useMemo(
    () => ({
      id: 'dashboard_1',
      title: 'Dashboard Title',
      widgets: [
        {
          type: 'card',
          i: 'AlertSummary',
          x: 0,
          y: 0,
          w: 5,
          h: 7,
          minH: 2,
          minW: 2,
          isResizable: false,
          static: true,
        },

        {
          type: 'line-chart',
          i: 'top_open_issues',
          x: 5,
          y: 0,
          w: 7,
          h: 7,
          minH: 2,
          minW: 2,
          isResizable: false,
          static: true,
        },
        {
          type: 'line-chart',
          i: 'critical_issues',
          x: 0,
          y: 7,
          w: 6,
          h: 7,
          minH: 4,
          minW: 2,
          isResizable: false,
          static: true,
        },
        {
          type: 'line-chart',
          i: 'created_vs_resolved_issues',
          x: 6,
          y: 7,
          w: 6,
          h: 7,
          minH: 4,
          minW: 2,
          isResizable: false,
          static: true,
        },
      ],
      available: availableWidgets,
    }),
    [availableWidgets],
  );

  return { dashboardWidgets, availableWidgets, alertSummaryWidgets };
};
